import { getStoredToken, getStoredUser } from './auth';
import { oidcManager } from './oidc';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';
const FALLBACK_DEV_USER = 'admin@netra.local';

function currentDevUser(): string {
  return getStoredUser()?.email || FALLBACK_DEV_USER;
}

async function authHeaders(): Promise<Record<string, string>> {
  // 1) Local NETRA JWT (email+password login)
  const localToken = getStoredToken();
  if (localToken) {
    return { Authorization: `Bearer ${localToken}` };
  }
  // 2) OIDC / Keycloak token
  try {
    const u = await oidcManager.getUser();
    if (u && !u.expired && u.access_token) {
      return { Authorization: `Bearer ${u.access_token}` };
    }
  } catch { /* fallback */ }
  // 3) Dev-bypass header (only honoured when APP_DEBUG=true on backend)
  return { 'X-Dev-User': currentDevUser() };
}

export class APIError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_URL}${path}`;
  const ah = await authHeaders();
  const resp = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...ah,
      ...(init.headers || {}),
    },
  });
  if (!resp.ok) {
    const body = await resp.text();
    throw new APIError(resp.status, `${resp.status} ${resp.statusText}`, body);
  }
  const ct = resp.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    return resp.json();
  }
  return (await resp.text()) as unknown as T;
}

export async function apiStream(
  path: string,
  body: unknown,
  onToken: (token: string) => void,
): Promise<void> {
  const url = path.startsWith('http') ? path : `${API_URL}${path}`;
  const ah = await authHeaders();
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...ah,
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok || !resp.body) {
    throw new APIError(resp.status, await resp.text());
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const data = line.slice(6);
      if (data === '[DONE]') return;
      onToken(data);
    }
  }
}
